import React, { useState } from 'react';
import { DeleteOutlined, GiftOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import type { FormattedPrice } from '../../utils/format';
import type { IComboCartItem } from '../../types/combo.types';
import type { CartLinePricing } from '../../hooks/useCartPricing';
import { cartStyles as s } from './cart.styles';

interface ComboCartLineItemProps {
  item: IComboCartItem;
  pricing: CartLinePricing;
  price: (amount: number) => FormattedPrice;
  onChangeQuantity: (cartItemId: string, quantity: number) => void;
  onRemove: (cartItemId: string) => void;
}

/** Línea de promo en el carrito: mismo layout que CartLineItem con sus productos debajo. */
export const ComboCartLineItem: React.FC<ComboCartLineItemProps> = ({
  item,
  pricing,
  price,
  onChangeQuantity,
  onRemove,
}) => {
  const { combo, quantity, selections } = item;
  const [imageBroken, setImageBroken] = useState(false);

  return (
    <div style={s.line} className="cart-line">
      <div style={s.lineTop}>
        {combo.imageUrl && !imageBroken ? (
          <img src={combo.imageUrl} alt="" style={s.thumb} onError={() => setImageBroken(true)} />
        ) : (
          <span style={s.thumbFallback} aria-hidden="true">
            {combo.name.charAt(0).toUpperCase()}
          </span>
        )}

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={s.lineName}>{combo.name}</span>
          <span style={s.offerTag}>
            <GiftOutlined style={{ fontSize: 11, flexShrink: 0 }} />
            Promo
          </span>
          {combo.items.map((ci) => {
            const mods =
              selections.find((sel) => sel.productId === ci.productId)?.selectedModifiers ?? [];
            return (
              <span key={ci.productId} style={s.lineModifier}>
                {ci.quantity}x {ci.name}
                {mods.length > 0 && ` — ${mods.map((m) => `+ ${m.optionName}`).join(' · ')}`}
              </span>
            );
          })}
        </div>
      </div>

      <div style={s.lineBottom}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div className="cart-stepper">
            <button
              className="cart-stepper__btn"
              onClick={() => onChangeQuantity(item.cartItemId, quantity - 1)}
              aria-label="Disminuir cantidad"
            >
              <MinusOutlined style={{ fontSize: 12 }} />
            </button>
            <span key={quantity} className="cart-stepper__value cart-tick">
              {quantity}
            </span>
            <button
              className="cart-stepper__btn"
              onClick={() => onChangeQuantity(item.cartItemId, quantity + 1)}
              aria-label="Aumentar cantidad"
            >
              <PlusOutlined style={{ fontSize: 12 }} />
            </button>
          </div>

          <button
            className="cart-icon-btn cart-icon-btn--danger"
            onClick={() => onRemove(item.cartItemId)}
            aria-label={`Eliminar ${combo.name}`}
          >
            <DeleteOutlined />
          </button>
        </div>

        {pricing.hasDiscount ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <span style={s.linePriceStrike}>{price(pricing.grossTotal).primary}</span>
            <span style={s.linePriceNet}>{price(pricing.netTotal).primary}</span>
          </div>
        ) : (
          <span style={s.linePrice}>{price(pricing.netTotal).primary}</span>
        )}
      </div>
    </div>
  );
};
